export const PERMISSIONS = {
  VIEW: "View",
  EDIT: "Edit",
  CREATE: "Create",
  IMPORT_EXPORT: "Import/Export",
  SUBMIT: "Submit",
  APPROVE: "Approve",
  RELEASE: "Release",
  ARCHIVE: "Archive",
  REJECT: "Reject",
  REASSIGN: "Reassign",
  ADMIN: "Admin",
  SUPERADMIN: "SuperAdmin",
};

const getStoredUserInfo = () => {
  try {
    const stored = localStorage.loggedinUserInfo;
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error("Failed to parse stored user info", error);
    return null;
  }
};

/**
 * Safely resolve the permission_to_edit object for the current user.
 */
export const resolveUserPermissions = (authUser) => {
  if (authUser?.permission_to_edit) {
    return authUser.permission_to_edit;
  }

  const stored = getStoredUserInfo();
  return stored?.permission_to_edit || {};
};

/**
 * Create a SuperAdmin permissions object with all permissions set to true.
 * This avoids Proxy invariant violations with read-only, non-configurable properties.
 */
const createSuperAdminPermissions = (originalPermissions) => {
  // Create a new object with all boolean properties set to true
  const superAdminPerms = {};
  
  // Copy all properties from original, setting booleans to true
  if (originalPermissions && typeof originalPermissions === "object") {
    for (const key in originalPermissions) {
      const value = originalPermissions[key];
      if (typeof value === "boolean") {
        superAdminPerms[key] = true;
      } else if (value && typeof value === "object" && !Array.isArray(value)) {
        // Recursively handle nested objects
        superAdminPerms[key] = createSuperAdminPermissions(value);
      } else {
        superAdminPerms[key] = value;
      }
    }
  }
  
  // Create a proxy that returns true for any undefined properties
  return new Proxy(superAdminPerms, {
    get(target, prop) {
      if (prop === "__raw") return originalPermissions;
      if (typeof prop === "symbol") {
        return Reflect.get(target, prop);
      }
      
      // If property exists, return it
      if (prop in target) {
        return Reflect.get(target, prop);
      }
      
      // For any undefined property, return true for SuperAdmin
      return true;
    },
    has() {
      return true;
    },
    ownKeys(target) {
      return Reflect.ownKeys(target);
    },
    getOwnPropertyDescriptor(target, prop) {
      const descriptor = Object.getOwnPropertyDescriptor(target, prop);
      if (descriptor) {
        return descriptor;
      }
      // For undefined properties, return a configurable descriptor
      return {
        configurable: true,
        enumerable: true,
        value: true,
        writable: true,
      };
    },
  });
};

/**
 * Build a reusable set of permission helpers for the provided user.
 */
export const buildPermissionContext = (authUser) => {
  const rawPermissions = resolveUserPermissions(authUser);
  // Check for SuperAdmin with case-insensitive fallback for backward compatibility
  const isSuperAdmin =
    rawPermissions?.[PERMISSIONS.SUPERADMIN] === true ||
    rawPermissions?.["Superadmin"] === true ||
    false;

  const permissions = isSuperAdmin
    ? createSuperAdminPermissions(rawPermissions || {})
    : rawPermissions || {};

  const has = (permissionKey) => {
    if (!permissionKey) return false;
    if (isSuperAdmin) return true;
    return permissions?.[permissionKey] === true;
  };

  const context = {
    permissions,
    has,
  };

  Object.defineProperties(context, {
    canView: {
      enumerable: true,
      get: () => has(PERMISSIONS.VIEW),
    },
    canEdit: {
      enumerable: true,
      get: () => has(PERMISSIONS.EDIT),
    },
    canCreate: {
      enumerable: true,
      get: () => has(PERMISSIONS.CREATE),
    },
    canSubmit: {
      enumerable: true,
      get: () => has(PERMISSIONS.SUBMIT),
    },
    canApprove: {
      enumerable: true,
      get: () => has(PERMISSIONS.APPROVE),
    },
    canRelease: {
      enumerable: true,
      get: () => has(PERMISSIONS.RELEASE),
    },
    canArchive: {
      enumerable: true,
      get: () => has(PERMISSIONS.ARCHIVE),
    },
    canReject: {
      enumerable: true,
      get: () => has(PERMISSIONS.REJECT),
    },
    canReassign: {
      enumerable: true,
      get: () => has(PERMISSIONS.REASSIGN),
    },
    canImportExport: {
      enumerable: true,
      get: () => has(PERMISSIONS.IMPORT_EXPORT),
    },
    isAdmin: {
      enumerable: true,
      get: () => has(PERMISSIONS.ADMIN),
    },
    isSuperAdmin: {
      enumerable: true,
      get: () => isSuperAdmin,
    },
  });

  return context;
};

/**
 * Convenience helper when only a single permission check is needed.
 */
export const hasPermission = (authUser, permissionKey) => {
  return buildPermissionContext(authUser).has(permissionKey);
};
